<template>
  <div class="task-time-tracker-record">
    <div class="task-time-tracker-record__current-time">
      <div v-if="record.Performer">
        <TirPmIconView
          :size="24"
          isRounded
          :isBordered="false"
          :colorCode="record.Performer.AvatarColor"
          :imageLoadUrl="record.Performer.Foto"
        >
          <template #mainSlot>
            {{ computedNameCompareFoto }}
          </template>
        </TirPmIconView>
      </div>
      <span class="task-time-tracker-record__date" v-if="dateEndStr"> {{ dateEndStr }}: </span>
      <span class="task-time-tracker-record__time">{{ timeStr }}</span>
    </div>
    <div class="task-time-tracker-record__controls" v-if="allowEdit">
      <TirPmButton
        :size="TirPmButtonSizeEnum.Small"
        :variant="TirPmButtonVariantEnum.LinkState"
        @click="emit('onDelete')"
        ><template #leftSlot><TrashIcon></TrashIcon></template>
      </TirPmButton>
      <TirPmButton
        :size="TirPmButtonSizeEnum.Small"
        :variant="TirPmButtonVariantEnum.LinkState"
        @click="emit('onEdit')"
        ><template #leftSlot><PencilSquareIcon></PencilSquareIcon></template>
      </TirPmButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, PropType } from 'vue';
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmIconView,
} from 'tir-components';
import { TrashIcon, PencilSquareIcon } from 'tir-style-system/icons/outline';
import { TaskTimeRecordModel } from './models';
import dateTransform from 'tir-pm-date-transform';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import GetAccessCurrentUserInQueueTaskByIdResponseModel from './models/GetAccessCurrentUserInQueueTaskByIdResponseModel';

const emit = defineEmits<{
  /**Удалить запись */
  (e: 'onDelete'): void;
  /**Редактировать запись */
  (e: 'onEdit'): void;
}>();

const props = defineProps({
  /**Запись таймера */
  record: {
    type: Object as PropType<TaskTimeRecordModel>,
    required: true,
  },
  /**Доступы юзера к очереди */
  userAccess: { type: Object as PropType<GetAccessCurrentUserInQueueTaskByIdResponseModel> },
});

const computedNameCompareFoto = computed(() => {
  if (!props.record.Performer) return;
  return (
    props.record.Performer.FirstName?.[0]?.toUpperCase() +
    props.record.Performer.LastName?.[0]?.toUpperCase()
  );
});
/**Разрешено редактирование времени */
const allowEdit = computed(() => {
  const isCurrentUser = props.record?.Performer?.Id == CurrentUser.Id;
  const isToday = props.record?.DateEnd?.getDate() == new Date().getDate();
  const isOwner = CurrentUser.Roles.includes(UserRoleEnum.Owner);

  return (isCurrentUser && isToday) || isOwner || props.userAccess.IsProjectAdmin;
});

/**Расчитать разницу */
const calculateTimeDifference = (date1: Date, date2: Date): { hours: number; minutes: number } => {
  // Получаем разницу в миллисекундах между двумя датами
  const diffInMs = Math.abs(date2.getTime() - date1.getTime());

  // Переводим разницу в часы и минуты
  const hours = Math.floor(diffInMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));

  return { hours, minutes };
};

const dateEndStr = computed(() =>
  props.record.DateEnd ? dateTransform(props.record.DateEnd).format('DD.MM.YYYY') : undefined,
);
/**Строка с временм */
const timeStr = computed(() => {
  let str = '';
  const dateStartTime = dateTransform(props.record.DateStart).format('HH:mm');
  if (props.record.DateEnd) {
    const { hours, minutes } = calculateTimeDifference(
      props.record.DateStart,
      props.record.DateEnd,
    );
    str += `${dateStartTime}-${dateTransform(props.record.DateEnd).format(
      'HH:mm',
    )} (${`${hours}ч.`} ${`${minutes}мин.`})`;
  } else {
    str += dateStartTime;
  }

  return str;
});
</script>

<style lang="scss" scoped>
@import './style/task-time-tracker-record.style.scss';
</style>
